/**
 * SKU matrix helpers — aligned with merchant-app product_spec_draft.dart
 */

export function hasPropertyName(prop) {
  const zh = (prop.zh_name || "").trim();
  const ug = (prop.ug_name || "").trim();
  return zh.length > 0 || ug.length > 0;
}

export function getPropertyLabel(prop, isZh) {
  const zh = (prop.zh_name || "").trim();
  const ug = (prop.ug_name || "").trim();
  if (isZh) return zh || ug;
  return ug || zh;
}

export function normalizeGroupProperties(group) {
  const raw =
    group?.product_property ??
    group?.productProperty ??
    group?.childList ??
    [];
  if (Array.isArray(raw)) return raw;
  return Object.values(raw);
}

export function mapGroupFromApi(group, index) {
  return {
    ...group,
    childList: normalizeGroupProperties(group),
    index,
  };
}

export function findPropertyInGroup(group, token) {
  if (!token) return null;
  const key = String(token).trim();
  return group.childList?.find(
    (p) =>
      String(p.id) === key ||
      p.zh_name?.trim() === key ||
      p.ug_name?.trim() === key
  );
}

export function getConfiguredGroups(groups) {
  return (groups || []).filter(
    (g) =>
      g.id ||
      g.zh_name?.trim() ||
      g.ug_name?.trim() ||
      (Array.isArray(g.childList) && g.childList.length > 0)
  );
}

export function groupsHaveProperties(groups) {
  return (groups || []).some((g) => g.childList?.some(hasPropertyName));
}

function skusUseNameBasedCodes(skus) {
  if (!Array.isArray(skus) || skus.length === 0) return false;

  return skus.some((sku) => {
    const code = String(sku.code || "").trim();
    if (!code) return false;
    if (!/^\d+(-\d+)*$/.test(code)) return true;
    return false;
  });
}

function propertyCodeToken(prop, preferNames = false) {
  if (!preferNames && prop.id != null) return String(prop.id);
  return prop.zh_name?.trim() || prop.ug_name?.trim() || (prop.id != null ? String(prop.id) : "");
}

/** Merchant product-sku API returns yuan; DB stores cents. */
export function formatSkuPriceYuanInput(value) {
  if (value == null || value === "") return "";
  const normalized = String(value).trim().replace(",", ".");
  const raw = parseFloat(normalized);
  if (!Number.isFinite(raw) || raw <= 0) return "";
  if (raw === Math.floor(raw)) return String(Math.floor(raw));
  return raw.toFixed(2);
}

export function parseYuanInputToCents(raw) {
  const normalized = String(raw ?? "").trim().replace(",", ".");
  const value = parseFloat(normalized);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round(value * 100);
}

function skuPriceFromApi(sku) {
  return formatSkuPriceYuanInput(sku?.price);
}

function resolveFormPriceYuan(snapPrice, rowPrice) {
  if (snapPrice != null && String(snapPrice).trim() !== "") {
    return String(snapPrice);
  }
  return formatSkuPriceYuanInput(rowPrice);
}

export function skuRowLabelKey(labels) {
  return (labels || []).join("/");
}

function resolveExistingSku(skuByCode, skus, code, labels) {
  const key = String(code);
  if (skuByCode[key]) return skuByCode[key];

  const labelKey = skuRowLabelKey(labels);
  for (const sku of skus || []) {
    if (String(sku.code) === key) return sku;
    if (!sku.code || !labels?.length) continue;

    const skuCode = String(sku.code);
    const parts = skuCode.split("-");
    if (parts.length === labels.length) {
      const skuLabelKey = parts.join("/");
      if (skuLabelKey === labelKey) return sku;
    }
    if (skuCode.replace(/-/g, "/") === labelKey) return sku;
  }

  return null;
}

function buildCartesianMatrix(configuredGroups, existingSkus, isZh, preferNameCodes = false) {
  const skus = existingSkus || [];
  const skuByCode = {};
  skus.forEach((sku) => {
    if (sku.code != null) skuByCode[String(sku.code)] = sku;
  });

  const lists = configuredGroups.map((g) =>
    g.childList.filter(hasPropertyName)
  );

  const combine = (index, tokens, labels, propertyIds) => {
    if (index === lists.length) {
      const code = tokens.join("-");
      const existing = resolveExistingSku(skuByCode, skus, code, labels);
      return [
        {
          code,
          labels: [...labels],
          propertyIds: [...propertyIds],
          price: skuPriceFromApi(existing),
          quantity: existing?.quantity ?? 0,
          product_no: existing?.product_no ?? existing?.productNo ?? "",
        },
      ];
    }

    return lists[index].flatMap((prop) =>
      combine(
        index + 1,
        [...tokens, propertyCodeToken(prop, preferNameCodes)],
        [...labels, getPropertyLabel(prop, isZh)],
        [...propertyIds, prop.id]
      )
    );
  };

  return combine(0, [], [], []);
}

export function buildMatrixFromExistingSkus(groups, skus, isZh) {
  if (!Array.isArray(skus) || skus.length === 0) return [];

  const configuredGroups = getConfiguredGroups(groups);
  const multiPartSkus = skus.filter((sku) => String(sku.code || "").includes("-"));
  const maxParts =
    multiPartSkus.length > 0
      ? Math.max(...multiPartSkus.map((sku) => String(sku.code).split("-").length))
      : 1;
  const groupCount = Math.max(configuredGroups.length, maxParts > 1 ? maxParts : 1);
  const candidates =
    groupCount > 1 ? filterSkusForGroups(skus, groupCount) : skus;

  return candidates.map((sku) => {
    const code = String(sku.code);
    const parts = code.split("-");
    const labels = parts.map((part, idx) => {
      const group = configuredGroups[idx] || configuredGroups[0] || groups[idx] || groups[0];
      const prop = group ? findPropertyInGroup(group, part) : null;
      return prop ? getPropertyLabel(prop, isZh) : part;
    });

    return {
      code,
      labels,
      propertyIds: parts.map((part, idx) => {
        const group = configuredGroups[idx] || configuredGroups[0] || groups[idx] || groups[0];
        return group ? findPropertyInGroup(group, part)?.id ?? null : null;
      }),
      price: skuPriceFromApi(sku),
      quantity: sku?.quantity ?? 0,
      product_no: sku?.product_no ?? sku?.productNo ?? "",
    };
  });
}

export function shouldReconstructGroupsFromSkus(groups, skus, isZh) {
  if (!Array.isArray(skus) || skus.length === 0) return false;
  if (!groupsHaveProperties(groups)) return true;

  const multiPartSkus = skus.filter((sku) => String(sku.code || "").includes("-"));
  if (multiPartSkus.length === 0) return false;

  const maxParts = Math.max(
    ...multiPartSkus.map((sku) => String(sku.code).split("-").length)
  );
  const configuredCount = getConfiguredGroups(groups).length;

  if (maxParts > 1 && configuredCount < maxParts) {
    return true;
  }

  return buildSkuMatrix(groups, skus, isZh, { allowExistingFallback: false }).length === 0;
}

export function normalizeGroupsFromApi(groups) {
  return (groups || []).map((group, index) =>
    Array.isArray(group?.childList) ? group : mapGroupFromApi(group, index)
  );
}

/** Copy API groups into draft entities (no ids) for product clone. */
export function toDraftGroupsFromApi(groups) {
  return normalizeGroupsFromApi(groups).map((group, index) => ({
    id: null,
    zh_name: group.zh_name || "",
    ug_name: group.ug_name || "",
    order: group.order ?? index,
    index,
    isDraft: true,
    childList: (group.childList || []).filter(hasPropertyName).map((prop, i) => ({
      id: null,
      zh_name: prop.zh_name || "",
      ug_name: prop.ug_name || "",
      order: prop.order ?? i,
      image: draftPropertyImagePath(prop),
      isDraft: true,
    })),
  }));
}

function draftPropertyImagePath(prop) {
  if (prop?.image_media?.file_path) return prop.image_media.file_path;
  if (prop?.image_media?.path) return prop.image_media.path;
  const img = prop?.image;
  if (typeof img === "string" && img && !/^https?:\/\//i.test(img)) {
    return img;
  }
  return null;
}

/** Snapshot price/qty/product_no keyed by label (and code) from API groups+skus. */
export function snapshotSkuValuesFromApiGroups(groups, skuList, isZh) {
  const snapshot = {};
  buildMatrixForDisplay(groups, skuList, isZh).forEach((row) => {
    const entry = {
      price: row.price,
      quantity: row.quantity,
      productNo: row.product_no,
    };
    snapshot[skuRowLabelKey(row.labels)] = entry;
    if (row.code) snapshot[row.code] = entry;
  });
  return snapshot;
}

/** Prefer DB property groups; only reconstruct from SKUs when API returned none. */
export function resolveGroupsForProductSpecs(groups, skus, isZh) {
  const mapped = normalizeGroupsFromApi(groups);

  if (mapped.length > 0) {
    return mapped;
  }

  if (!Array.isArray(skus) || skus.length === 0) {
    return mapped;
  }

  return reconstructGroupsFromSkus(skus, isZh);
}

export function getDisplayGroupsForProduct(groups, skus, isZh) {
  const normalized = normalizeGroupsFromApi(groups);
  if (normalized.length > 0) {
    return normalized;
  }
  if (Array.isArray(skus) && skus.length > 0) {
    return reconstructGroupsFromSkus(skus, isZh);
  }
  return normalized;
}

function matrixRowMatchesSku(row, skus) {
  const skuByCode = {};
  (skus || []).forEach((sku) => {
    if (sku.code != null) skuByCode[String(sku.code)] = sku;
  });
  return Boolean(resolveExistingSku(skuByCode, skus, row.code, row.labels));
}

/** Build SKU table rows, preferring rows that match existing SKU API data. */
export function buildMatrixForDisplay(groups, existingSkus, isZh) {
  const cartesian = buildSkuMatrix(groups, existingSkus, isZh, {
    allowExistingFallback: false,
  });

  if (!Array.isArray(existingSkus) || existingSkus.length === 0) {
    return cartesian.length > 0
      ? cartesian
      : buildSkuMatrix(groups, existingSkus, isZh);
  }

  const fromSkus = buildMatrixFromExistingSkus(groups, existingSkus, isZh);
  if (cartesian.length === 0) {
    return fromSkus;
  }

  const cartesianMatched = cartesian.filter((row) =>
    matrixRowMatchesSku(row, existingSkus)
  ).length;

  if (cartesianMatched === 0 && fromSkus.length > 0) {
    return fromSkus;
  }

  if (fromSkus.length > cartesianMatched) {
    return fromSkus;
  }

  return cartesian;
}

export function isCompleteSkuCode(code, groupCount) {
  if (groupCount <= 1) return true;
  const parts = String(code).split("-").filter(Boolean);
  return parts.length === groupCount;
}

export function filterSkusForGroups(skus, groupCount) {
  if (!Array.isArray(skus) || groupCount <= 1) return skus || [];
  return skus.filter((sku) => isCompleteSkuCode(sku.code, groupCount));
}

export function reconstructGroupsFromSkus(skus, isZh) {
  if (!Array.isArray(skus) || skus.length === 0) return [];

  const codes = skus
    .map((s) => String(s.code || "").trim())
    .filter(Boolean);
  const multiPartCodes = codes.filter((c) => c.includes("-"));

  if (multiPartCodes.length === 0) {
    const values = [...new Set(codes)];
    return [
      {
        id: null,
        zh_name: isZh ? "规格" : "ئۆلچەم",
        ug_name: isZh ? "规格" : "ئۆلچەم",
        childList: values.map((v, i) => ({
          id: null,
          zh_name: v,
          ug_name: v,
          order: i,
          isDraft: true,
        })),
        isDraft: true,
        index: 0,
      },
    ];
  }

  const partCount = Math.max(
    ...multiPartCodes.map((c) => c.split("-").length)
  );
  const buckets = Array.from({ length: partCount }, () => new Set());

  // Only derive property values from complete SKU codes (e.g. 绿色-M),
  // not orphan partial codes (e.g. 绿色 alone).
  multiPartCodes.forEach((code) => {
    const parts = code.split("-");
    parts.forEach((part, idx) => {
      if (part && idx < partCount) buckets[idx].add(part);
    });
  });

  return buckets.map((bucket, groupIndex) => ({
    id: null,
    zh_name: isZh ? `规格${groupIndex + 1}` : `ئۆلچەم ${groupIndex + 1}`,
    ug_name: isZh ? `规格${groupIndex + 1}` : `ئۆلچەم ${groupIndex + 1}`,
    childList: Array.from(bucket).map((v, i) => ({
      id: null,
      zh_name: v,
      ug_name: v,
      order: i,
      isDraft: true,
    })),
    isDraft: true,
    index: groupIndex,
  }));
}

export function buildSkuMatrix(groups, existingSkus, isZh, options = {}) {
  const { allowExistingFallback = true } = options;
  const configuredGroups = getConfiguredGroups(groups);
  const configuredCount = configuredGroups.length;
  const preferNameCodes = skusUseNameBasedCodes(existingSkus);

  if (configuredCount >= 2) {
    const allGroupsReady = configuredGroups.every((g) =>
      g.childList?.some(hasPropertyName)
    );
    if (!allGroupsReady) {
      return allowExistingFallback
        ? buildMatrixFromExistingSkus(groups, existingSkus, isZh)
        : [];
    }

    const validSkus = filterSkusForGroups(existingSkus, configuredCount);
    const matrix = buildCartesianMatrix(
      configuredGroups,
      validSkus,
      isZh,
      preferNameCodes
    );
    if (matrix.length === 0 && allowExistingFallback) {
      return buildMatrixFromExistingSkus(groups, existingSkus, isZh);
    }
    return matrix;
  }

  const activeGroups = groups.filter((g) =>
    g.childList?.some((p) => hasPropertyName(p))
  );
  if (activeGroups.length === 0) {
    return allowExistingFallback
      ? buildMatrixFromExistingSkus(groups, existingSkus, isZh)
      : [];
  }

  const group = activeGroups[0];
  const matrix = buildCartesianMatrix(
    [group],
    existingSkus,
    isZh,
    preferNameCodes
  );
  if (matrix.length === 0 && allowExistingFallback) {
    return buildMatrixFromExistingSkus(groups, existingSkus, isZh);
  }
  return matrix;
}

export function buildSkuPayloadFromForm(groups, skuList, formValues, isZh) {
  if (!groups?.length) {
    return skuMapsFromFormValues(formValues, null, isZh);
  }

  const snapshot = snapshotSkuValuesByLabelFromGroups(
    groups,
    formValues,
    isZh
  );
  const cleanValues = buildSkuFormValues(groups, skuList, isZh, snapshot);
  return skuMapsFromFormValues(cleanValues, groups, isZh, skuList);
}

export function buildSkuPayloadFromFormOrValues(groups, skuList, formValues, isZh) {
  const payload = buildSkuPayloadFromForm(groups, skuList, formValues, isZh);
  if (payload) return payload;

  const propertyIds = formValues?.property_id || {};
  const codes = Object.keys(propertyIds);
  if (codes.length === 0) return null;

  const multiPartCodes = codes.filter((code) => code.includes("-"));
  const maxParts =
    multiPartCodes.length > 0
      ? Math.max(...multiPartCodes.map((code) => code.split("-").length))
      : 1;

  const filteredCodes = codes.filter((code) => {
    if (maxParts <= 1) return true;
    if (!code.includes("-")) return false;
    return code.split("-").filter(Boolean).length === maxParts;
  });

  if (filteredCodes.length === 0) return null;

  const propertyPrices = formValues.property_price || {};
  const propertyQuantities = formValues.property_quantity || {};
  const propertyProductNos = formValues.property_product_no || {};
  const ids = {};
  const prices = {};
  const quantities = {};
  const productNos = {};

  filteredCodes.forEach((code) => {
    ids[code] = String(propertyIds[code] ?? code);
    prices[code] = String(propertyPrices[code] ?? "");
    quantities[code] = String(propertyQuantities[code] ?? "");
    const no = propertyProductNos[code];
    if (no != null && String(no).trim() !== "") {
      productNos[code] = String(no).trim();
    }
  });

  return {
    property_id: ids,
    property_price: prices,
    property_quantity: quantities,
    property_product_no: productNos,
  };
}

export function syncSkuFormFields(formRef, groups, skuList, isZh, extraSnapshot = {}) {
  const form = formRef?.current || formRef;
  if (!form?.setFieldsValue) return null;

  const currentValues = form.getFieldsValue?.(true) || {};
  const snapshot = {
    ...snapshotSkuValuesByLabelFromGroups(groups, currentValues, isZh),
    ...extraSnapshot,
  };
  const cleanValues = buildSkuFormValues(groups, skuList, isZh, snapshot);
  form.setFieldsValue(cleanValues);
  return cleanValues;
}

export function skuListSignature(skuList) {
  return JSON.stringify(
    (skuList || []).map((sku) => [
      sku.code,
      sku.price,
      sku.quantity,
      sku.product_no ?? sku.productNo,
    ])
  );
}

export function skuMapsFromFormValues(values, groups, isZh, skuList = null) {
  const propertyIds = values.property_id || {};
  const propertyPrices = values.property_price || {};
  const propertyQuantities = values.property_quantity || {};
  const propertyProductNos = values.property_product_no || {};

  let allowedCodes = null;
  if (groups?.length) {
    const matrix =
      skuList != null
        ? buildMatrixForDisplay(groups, skuList, isZh)
        : buildSkuMatrix(groups, [], isZh);
    allowedCodes = new Set(matrix.map((row) => row.code));
  }

  let codes = Object.keys(propertyIds).filter((code) => {
    if (!allowedCodes || allowedCodes.size === 0) return true;
    return allowedCodes.has(code);
  });

  // Matrix codes may not match form keys right after persist — keep raw form keys.
  if (codes.length === 0 && Object.keys(propertyIds).length > 0) {
    codes = Object.keys(propertyIds);
  }
  if (codes.length === 0) return null;

  const ids = {};
  const prices = {};
  const quantities = {};
  const productNos = {};

  codes.forEach((code) => {
    ids[code] = String(propertyIds[code] ?? code);
    prices[code] = String(propertyPrices[code] ?? "");
    quantities[code] = String(propertyQuantities[code] ?? "");
    const no = propertyProductNos[code];
    if (no != null && String(no).trim() !== "") {
      productNos[code] = String(no).trim();
    }
  });

  return {
    property_id: ids,
    property_price: prices,
    property_quantity: quantities,
    property_product_no: productNos,
  };
}

export function firstSkuPriceCents(formValues, groups, isZh) {
  let prices = formValues?.property_price || {};
  if (groups?.length) {
    const payload = buildSkuPayloadFromForm(groups, [], formValues, isZh);
    prices = payload?.property_price || {};
  }

  const codes = Object.keys(prices);
  for (const code of codes) {
    const cents = parseYuanInputToCents(prices[code]);
    if (cents > 0) return cents;
  }
  return 0;
}

export function snapshotSkuValuesByLabelFromGroups(groups, formValues, isZh) {
  const matrix = buildSkuMatrix(groups, [], isZh);
  const prices = formValues?.property_price || {};
  const quantities = formValues?.property_quantity || {};
  const productNos = formValues?.property_product_no || {};
  const snapshot = {};

  matrix.forEach((row) => {
    snapshot[skuRowLabelKey(row.labels)] = {
      price: prices[row.code] ?? "",
      quantity: quantities[row.code] ?? "",
      productNo: productNos[row.code] ?? "",
    };
    snapshot[row.code] = snapshot[skuRowLabelKey(row.labels)];
  });

  return snapshot;
}

function formatQuantityForInput(value) {
  if (value == null || value === "") return "";
  return String(value);
}

export function buildSkuFormValues(groups, skuList, isZh, valueSnapshot = {}) {
  const matrix = buildMatrixForDisplay(groups, skuList, isZh);
  const property_id = {};
  const property_price = {};
  const property_quantity = {};
  const property_product_no = {};

  matrix.forEach((row) => {
    const labelKey = skuRowLabelKey(row.labels);
    const code = row.code;
    const snap =
      valueSnapshot[labelKey] ||
      valueSnapshot[code] ||
      {
        price: row.price,
        quantity: row.quantity,
        productNo: row.product_no,
      };

    property_id[code] = code;
    property_price[code] = resolveFormPriceYuan(snap.price, row.price);
    property_quantity[code] =
      snap.quantity != null && String(snap.quantity).trim() !== ""
        ? formatQuantityForInput(snap.quantity)
        : formatQuantityForInput(row.quantity);
    const productNo = snap.productNo ?? row.product_no;
    if (productNo != null && String(productNo).trim() !== "") {
      property_product_no[code] = String(productNo).trim();
    }
  });

  return {
    property_id,
    property_price,
    property_quantity,
    property_product_no,
  };
}

export function totalSkuQuantity(formValues, groups, isZh) {
  let quantities = formValues?.property_quantity || {};
  if (groups?.length) {
    const payload = buildSkuPayloadFromForm(groups, [], formValues, isZh);
    quantities = payload?.property_quantity || {};
  }

  let total = 0;
  Object.keys(quantities).forEach((code) => {
    total += parseInt(quantities[code], 10) || 0;
  });
  return total;
}

export function getSkuPriceBoundsFromValues(groups, skuList, formValues, isZh) {
  const payload = buildSkuPayloadFromForm(groups, skuList, formValues, isZh);
  const prices = payload?.property_price || {};
  const values = Object.values(prices)
    .map((raw) => parseFloat(String(raw).trim().replace(",", ".")))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (values.length === 0) {
    return null;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);

  return {
    min,
    max,
    hasRange: min !== max,
  };
}

export function getProductSkuPriceBoundsFromApi(product) {
  if (!product) return null;

  const minPrice = Number(product.min_price);
  const maxPrice = Number(product.max_price);
  if (Number.isFinite(minPrice) && Number.isFinite(maxPrice) && minPrice > 0) {
    return {
      minCents: minPrice,
      maxCents: maxPrice,
      hasRange: minPrice !== maxPrice,
    };
  }

  const skus = product.product_skus;
  if (!Array.isArray(skus) || skus.length === 0) {
    return null;
  }

  const prices = skus
    .map((sku) => Number(sku?.price))
    .filter((price) => Number.isFinite(price) && price > 0);

  if (prices.length === 0) {
    return null;
  }

  const minCents = Math.min(...prices);
  const maxCents = Math.max(...prices);

  return {
    minCents,
    maxCents,
    hasRange: minCents !== maxCents,
  };
}

export function formatSkuPriceRangeYuan(bounds, { symbol = "￥" } = {}) {
  if (!bounds) return "";

  const formatCents = (value) => {
    const raw = Number(value);
    if (!Number.isFinite(raw)) return "";
    const yuan = raw / 100;
    if (yuan === Math.floor(yuan)) return `${symbol}${Math.floor(yuan)}`;
    return `${symbol}${yuan.toFixed(2)}`;
  };

  const formatYuan = (value) => {
    const raw = Number(value);
    if (!Number.isFinite(raw)) return "";
    if (raw === Math.floor(raw)) return `${symbol}${Math.floor(raw)}`;
    return `${symbol}${raw.toFixed(2)}`;
  };

  if ("minCents" in bounds) {
    if (bounds.hasRange) {
      return `${formatCents(bounds.minCents)}-${formatCents(bounds.maxCents)}`;
    }
    return formatCents(bounds.minCents);
  }

  if (bounds.hasRange) {
    return `${formatYuan(bounds.min)}-${formatYuan(bounds.max)}`;
  }
  return formatYuan(bounds.min);
}

export function hasSkuFormPayload(values) {
  return Object.keys(values?.property_id || {}).length > 0;
}

export function pickSkuFormValues(preferred, fallback) {
  if (hasSkuFormPayload(preferred)) return preferred;
  if (hasSkuFormPayload(fallback)) return fallback;
  return preferred || fallback;
}

export function extractCreatedPropertyId(res) {
  const created =
    res?.data?.property ??
    res?.data?.data?.property ??
    res?.data;
  if (created?.id != null) return created.id;
  if (res?.data?.id != null) return res.data.id;
  return null;
}

export function extractCreatedGroupId(res) {
  if (res?.data?.id != null) return res.data.id;
  if (res?.data?.data?.id != null) return res.data.data.id;
  return null;
}

/** Ensure we have spec groups to persist even when only the SKU form was filled. */
export function ensurePersistableGroups(groups, formValues, isZh) {
  const normalized = normalizeGroupsFromApi(groups || []);
  if (normalized.some((g) => g.childList?.some(hasPropertyName))) {
    return normalized;
  }

  const propertyIds = formValues?.property_id || {};
  const codes = Object.keys(propertyIds).filter(Boolean);
  if (codes.length === 0) {
    return normalized;
  }

  return reconstructGroupsFromSkus(
    codes.map((code) => ({ code })),
    isZh
  );
}
